import React from 'react';
import { DeckTheme } from './types';
import './deck.css';

const FONT_CSS = /^https:\/\/(api\.fontshare\.com|fonts\.googleapis\.com|rsms\.me)\/[^"'<>\s]+$/;
const loading = new Map<string, Promise<void>>();

// #region loadFontCss
/** Adds the theme's font stylesheet to <head> once; resolves when it has loaded (or failed). */
export function loadFontCss(url: string | null | undefined): Promise<void> {
    if (!url || !FONT_CSS.test(url) || typeof document === 'undefined') return Promise.resolve();
    const known = loading.get(url);
    if (known) return known;
    const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((l) => (l as HTMLLinkElement).href === url);
    const p = existing ? Promise.resolve() : new Promise<void>((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.onload = () => resolve();
        link.onerror = () => resolve();
        document.head.appendChild(link);
    });
    loading.set(url, p);
    return p;
}

// #region useDeckFont
export function useDeckFont(url: string | null | undefined): void {
    React.useEffect(() => { void loadFontCss(url); }, [url]);
}

// #region themeVars
/** The theme as CSS custom properties; every slide style reads these, so a theme is all a brand needs. */
export function themeVars(t: DeckTheme): React.CSSProperties {
    const from = t.frame?.from ?? t.accent;
    const to = t.frame?.to ?? t.accent_dark;
    return {
        '--wd-accent': t.accent,
        '--wd-accent-dark': t.accent_dark,
        '--wd-ink': t.ink,
        '--wd-body': t.body,
        '--wd-muted': t.muted,
        '--wd-page': t.page,
        '--wd-card': t.card,
        '--wd-line': t.line,
        '--wd-eyebrow': t.eyebrow || t.accent,
        '--wd-secondary': t.secondary || t.accent_dark,
        '--wd-font': t.font,
        '--wd-heading-font': t.heading_font || t.font,
        '--wd-heading-weight': String(t.heading_weight || 600),
        '--wd-heading-tracking': `${t.heading_tracking ?? 0}em`,
        // The brand kit's radii are email pixels (a 600px-wide email shown 1:1); a slide is designed at
        // 1920px and shown at about half that, so they are doubled to look the same on screen.
        '--wd-radius': `${Math.max(0, Math.min(96, (t.radius ?? 16) * 2))}px`,
        '--wd-button-radius': `${Math.max(0, Math.min(96, (t.button_radius ?? t.radius ?? 16) * 2))}px`,
        '--wd-frame-from': from,
        '--wd-frame-to': to,
        '--wd-frame': `linear-gradient(135deg, ${from}, ${to})`
    } as React.CSSProperties;
}

// #region DeckThemeStyle
/**
 * Loads the theme's font stylesheet and defines its CSS variables on `selector` (default `.wd-themed`), for chrome
 * around the slides (viewer header, editor panels). SlideView sets the same variables on itself, so slides never need it.
 */
export const DeckThemeStyle: React.FunctionComponent<{ theme: DeckTheme; selector?: string }> = ({ theme, selector = '.wd-themed' }): React.ReactElement => {
    useDeckFont(theme.font_css_url);
    const vars = themeVars(theme) as Record<string, string>;
    const css = `${selector}{${Object.entries(vars).map(([k, v]) => `${k}:${String(v).replace(/[<>{}]/g, '')}`).join(';')}}`;
    return <style>{css}</style>;
};
